import {
  Box,
  Center,
  HStack,
  IconButton,
  SimpleGrid,
  Spinner,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useDeleteAssetMutation } from 'hooks/mutations/useDeleteAssetMutation';
import { useRedeemTokenRewardMutation } from 'hooks/mutations/useRedeemTokenRewardMutation';
import { useUploadAssetMutation } from 'hooks/mutations/useUploadAssetMutation';
import { useGetUserAssetsQuery } from 'hooks/queries/useGetUserAssetsQuery';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { useCallback, useRef, useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import Icon from 'components/Icon';
import { TokensModal } from 'components/Modal/TokensModal';
import Text from 'components/Text';
import { ERoute } from 'types/frontend';

import DevSandboxTooltip from '../../DevSandboxTooltip';

export const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/gif', 'image/webp', 'application/json'];
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB per file

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const validateFile = (
  file: { name: string; type: string; size: number },
  storageUsedBytes: number,
  storageLimitBytes: number
): { valid: boolean; error?: string; fileName?: string } => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, fileName: file.name, error: 'Unsupported type. Use PNG, JPG, SVG, GIF, WEBP, or JSON.' };
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return { valid: false, fileName: file.name, error: `Too large (${formatBytes(file.size)}). Max 10 MB per file.` };
  }

  const remainingBytes = storageLimitBytes - storageUsedBytes;
  if (file.size > remainingBytes) {
    return { valid: false, fileName: file.name, error: `Not enough storage (${formatBytes(remainingBytes)} left). Use BigTokens to expand.` };
  }

  return { valid: true };
};

interface IYourFilesTabProps {
  isFreeUser: boolean;
  entityType?: string;
  entityId?: string;
}

const YourFilesTab = ({ isFreeUser, entityType, entityId }: IYourFilesTabProps) => {
  const { id: userId, storageLimitBytes, storageUsedBytes, bigTokensCollected } = useCurrentUser();
  const { toastError, toastSuccess } = useToastWrapper();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<{ fileName: string; message: string } | null>(null);
  const [deletingAssetId, setDeletingAssetId] = useState<string | null>(null);
  const { isOpen: isTokensModalOpen, onOpen: onTokensModalOpen, onClose: onTokensModalClose } = useDisclosure();

  const { assets, isPending } = useGetUserAssetsQuery({
    userId,
    entityType,
    entityId,
  });

  const uploadMutation = useUploadAssetMutation();
  const deleteMutation = useDeleteAssetMutation();
  const redeemMutation = useRedeemTokenRewardMutation();

  const storageLimit = storageLimitBytes || 10485760;
  const usagePercent = Math.min(100, Math.round((storageUsedBytes / storageLimit) * 100));
  const isStorageFull = storageUsedBytes >= storageLimit;
  const hasTokens = bigTokensCollected > 0;

  const validateAndUpload = useCallback(
    (files: FileList | File[]) => {
      if (!entityType || !entityId) {
        toastError('Missing entity context for upload');
        return;
      }

      setUploadError(null);

      let projectedUsedBytes = storageUsedBytes;
      let lastError: { fileName: string; message: string } | null = null;
      const uploadPromises: Promise<unknown>[] = [];

      Array.from(files).forEach((file) => {
        const result = validateFile(file, projectedUsedBytes, storageLimit);

        if (!result.valid) {
          lastError = { fileName: result.fileName || file.name, message: result.error || 'Upload failed' };
          return;
        }

        projectedUsedBytes += file.size;
        uploadPromises.push(
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          uploadMutation.mutateAsync({ file, entityType, entityId }).catch(() => { /* handled by mutation onError */ })
        );
      });

      if (lastError) {
        const pendingError = lastError;
        if (uploadPromises.length > 0) {
          Promise.allSettled(uploadPromises).then(() => setUploadError(pendingError));
        } else {
          setUploadError(pendingError);
        }
      }
    },
    [entityType, entityId, uploadMutation, toastError, storageUsedBytes, storageLimit]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files.length > 0) {
        validateAndUpload(e.dataTransfer.files);
      }
    },
    [validateAndUpload]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        validateAndUpload(e.target.files);
        e.target.value = '';
      }
    },
    [validateAndUpload]
  );

  const copyToClipboard = (text: string, label?: string) => {
    navigator.clipboard.writeText(text);
    toastSuccess(label || 'Copied to clipboard');
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Text fontSize="large" fontWeight="semibold" mb={2}>
          Your Files
        </Text>
        <Text fontSize="small" opacity="secondary">
          Upload and manage your own assets.
        </Text>
      </Box>

      {/* Inline upload error */}
      {uploadError && (
        <Box
          px={3}
          py={2}
          mb={3}
          bg="rgba(15,10,10,0.94)"
          border="1px solid"
          borderColor="rgba(248,113,113,0.4)"
          borderRadius="md"
        >
          <Box
            as="span"
            color="#fecaca"
            style={{ fontSize: '12px' }}
            lineHeight="1.4"
          >
            <Box as="span" color="#f87171" fontWeight={700}>{uploadError.fileName}</Box>
            {' — '}
            {uploadError.message}
          </Box>
        </Box>
      )}

      {/* Storage quota */}
      <Box
        mb={4}
        p={3}
        borderRadius="lg"
        bg={isStorageFull && !isFreeUser ? 'red.900' : 'whiteAlpha.50'}
        border="1px solid"
        borderColor={isStorageFull && !isFreeUser ? 'red.700' : 'whiteAlpha.100'}
        opacity={isFreeUser ? 0.5 : 1}
      >
        <HStack justifyContent="space-between" mb={2}>
          <HStack spacing={1.5}>
            <Icon type="FileStorage" size={16} />
            <Text fontSize="small" fontWeight="semibold">
              Storage
            </Text>
          </HStack>
          <HStack spacing={2}>
            <Text fontSize="micro" opacity="secondary">
              {formatBytes(storageUsedBytes)} / {formatBytes(storageLimit)}
            </Text>
            {!isFreeUser && (
              <Box
                as="button"
                onClick={() => {
                  if (!hasTokens) {
                    onTokensModalOpen();
                    return;
                  }
                  redeemMutation.mutate({ rewardType: 'increase-storage' });
                }}
                px={2}
                py={0.5}
                borderRadius="md"
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.200"
                cursor="pointer"
                transition="all 0.15s"
                _hover={{ bg: 'whiteAlpha.200', borderColor: 'purple.400' }}
                _active={{ transform: 'scale(0.96)' }}
              >
                {redeemMutation.isPending ? (
                  <Spinner size="xs" color="purple.300" />
                ) : (
                  <HStack spacing="4px">
                    <Icon type="Bigtokens" size={12} />
                    <Text fontSize="micro" fontWeight="semibold" color="purple.300">
                      +5 MB
                    </Text>
                  </HStack>
                )}
              </Box>
            )}
          </HStack>
        </HStack>
        <Box height="6px" bg="whiteAlpha.100" borderRadius="full" overflow="hidden">
          <Box
            width={`${usagePercent}%`}
            height="100%"
            borderRadius="full"
            bg={usagePercent >= 90 ? 'red.400' : usagePercent >= 70 ? 'orange.400' : 'purple.400'}
            transition="width 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
          />
        </Box>
        <Text fontSize="micro" opacity="tertiary" mt={1.5}>
          {isStorageFull && !isFreeUser
            ? 'Storage limit reached. Use BigTokens to expand.'
            : hasTokens
              ? '1 BigToken = +5 MB storage.'
              : 'Get BigTokens to expand your storage.'}
        </Text>
      </Box>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.svg,.gif,.webp,.json"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Upload zone (hidden when storage full) */}
      {(!isStorageFull || isFreeUser) && (
        <Center
          height="120px"
          border="2px dashed"
          borderColor={
            isDragging ? 'purple.400' : isFreeUser ? 'whiteAlpha.200' : 'whiteAlpha.300'
          }
          borderRadius="md"
          mb={4}
          cursor={isFreeUser ? 'default' : 'pointer'}
          bg={isDragging ? 'whiteAlpha.100' : 'transparent'}
          _hover={isFreeUser ? {} : { borderColor: 'purple.400', bg: 'whiteAlpha.50' }}
          transition="all 0.15s"
          opacity={isFreeUser ? 0.5 : 1}
          pointerEvents={isFreeUser ? 'none' : 'auto'}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <VStack spacing={1}>
            {uploadMutation.isPending ? (
              <>
                <Spinner size="sm" color="purple.400" />
                <Text fontSize="small" opacity="secondary">
                  Uploading...
                </Text>
              </>
            ) : (
              <>
                <Icon type="Add" size={24} />
                <Text fontSize="small" opacity="secondary">
                  Drag & drop or click to upload
                </Text>
                <Text fontSize="micro" opacity="tertiary">
                  PNG, JPG, SVG, GIF, WEBP, JSON
                </Text>
              </>
            )}
          </VStack>
        </Center>
      )}

      <TokensModal isOpen={isTokensModalOpen} onClose={onTokensModalClose} />

      {isFreeUser ? (
        <Box
          p={3}
          borderRadius="md"
          bgGradient="linear(to-r, purple.900, blue.900)"
          borderWidth="1px"
          borderColor="purple.600"
          mb={4}
        >
          <Text fontSize="small" fontWeight="semibold" mb={1}>
            Upload your own assets
          </Text>
          <Text fontSize="micro" opacity="secondary" mb={3}>
            Upload images, SVGs, JSON files, and more to use in your projects. No more character
            limit issues with inline assets!
          </Text>
          <Button
            scheme="gradient"
            size="sm"
            to={ERoute.Plans}
            leftIcon={<Icon type="Rocket" />}
            width="100%"
          >
            Upgrade to BIG to upload files
          </Button>
        </Box>
      ) : (
        <>
          {isPending ? (
            <Center py={8}>
              <Spinner size="sm" color="purple.400" />
            </Center>
          ) : assets.length === 0 ? (
            <Center py={8}>
              <Text fontSize="small" opacity="secondary">
                No files uploaded yet. Upload your first asset to get started!
              </Text>
            </Center>
          ) : (
            <SimpleGrid columns={2} spacing={3}>
              {assets.map((asset) => {
                const isSvg = asset.contentType === 'image/svg+xml';
                const isJson = asset.contentType === 'application/json';
                const isImage = asset.contentType.startsWith('image/') && !isSvg;

                return (
                  <Box
                    key={asset.id}
                    bg="whiteAlpha.100"
                    borderRadius="md"
                    overflow="hidden"
                    cursor="pointer"
                    _hover={{ bg: 'whiteAlpha.200' }}
                    transition="all 0.15s"
                    onClick={() => copyToClipboard(asset.publicUrl, 'Copied URL')}
                  >
                    {/* Thumbnail */}
                    {isImage && (
                      <Box
                        height="80px"
                        bg="whiteAlpha.50"
                        backgroundImage={`url(${asset.publicUrl})`}
                        backgroundSize="contain"
                        backgroundRepeat="no-repeat"
                        backgroundPosition="center"
                      />
                    )}
                    {isSvg && (
                      <Center height="80px" bg="whiteAlpha.50" p={2}>
                        <Box
                          as="img"
                          src={asset.publicUrl}
                          alt={asset.fileName}
                          maxH="100%"
                          maxW="100%"
                          objectFit="contain"
                          filter="brightness(0) invert(0.7)"
                        />
                      </Center>
                    )}
                    {isJson && (
                      <Center height="80px" bg="whiteAlpha.50">
                        <VStack spacing={0}>
                          <Box style={{ fontSize: '20px' }}>{'{ }'}</Box>
                          <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" color="whiteAlpha.500" style={{ fontSize: '8px' }}>JSON</Box>
                        </VStack>
                      </Center>
                    )}

                    {/* Info */}
                    <Box p={2}>
                      <Text
                        fontSize="micro"
                        fontWeight="semibold"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {asset.fileName}
                      </Text>
                      <HStack justifyContent="space-between" mt={1}>
                        <Text fontSize="micro" opacity="tertiary">
                          {formatBytes(asset.fileSizeBytes)}
                        </Text>
                        <HStack spacing={0}>
                          {isJson ? (
                            <>
                              <DevSandboxTooltip label="Copy JSON contents">
                                <IconButton
                                  aria-label="Copy JSON contents"
                                  icon={<Icon type="Code" size={14} />}
                                  variant="ghost"
                                  size="xs"
                                  _hover={{ bg: 'whiteAlpha.200' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    fetch(asset.publicUrl)
                                      .then((res) => res.text())
                                      .then((text) => copyToClipboard(text, 'Copied JSON contents'))
                                      .catch(() => copyToClipboard(asset.publicUrl, 'Copied URL'));
                                  }}
                                />
                              </DevSandboxTooltip>
                              <DevSandboxTooltip label="Copy URL">
                                <IconButton
                                  aria-label="Copy URL"
                                  icon={<Icon type="OutsideLink" size={14} />}
                                  variant="ghost"
                                  size="xs"
                                  _hover={{ bg: 'whiteAlpha.200' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(asset.publicUrl, 'Copied URL');
                                  }}
                                />
                              </DevSandboxTooltip>
                            </>
                          ) : (
                            <>
                              <DevSandboxTooltip label="Copy as <img> tag">
                                <IconButton
                                  aria-label="Copy as img tag"
                                  icon={<Icon type="Code" size={14} />}
                                  variant="ghost"
                                  size="xs"
                                  _hover={{ bg: 'whiteAlpha.200' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(
                                      `<img src="${asset.publicUrl}" alt="${asset.fileName}" />`,
                                      'Copied <img> tag'
                                    );
                                  }}
                                />
                              </DevSandboxTooltip>
                              <DevSandboxTooltip label="Copy as CSS background">
                                <IconButton
                                  aria-label="Copy as CSS background"
                                  icon={<Icon type="ColorsPalette" size={14} />}
                                  variant="ghost"
                                  size="xs"
                                  _hover={{ bg: 'whiteAlpha.200' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(
                                      `background-image: url('${asset.publicUrl}');`,
                                      'Copied CSS background'
                                    );
                                  }}
                                />
                              </DevSandboxTooltip>
                            </>
                          )}
                          <DevSandboxTooltip label="Delete file">
                            {deletingAssetId === asset.id ? (
                              <Box display="flex" alignItems="center" justifyContent="center" w="24px" h="24px">
                                <Spinner size="xs" color="red.300" />
                              </Box>
                            ) : (
                              <IconButton
                                aria-label="Delete file"
                                icon={<Icon type="Trash" size={14} />}
                                variant="ghost"
                                size="xs"
                                _hover={{ bg: 'red.900' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingAssetId(asset.id);
                                  deleteMutation.mutate({ assetId: asset.id }, {
                                    onSettled: () => setDeletingAssetId(null),
                                  });
                                }}
                              />
                            )}
                          </DevSandboxTooltip>
                        </HStack>
                      </HStack>
                    </Box>
                  </Box>
                );
              })}
            </SimpleGrid>
          )}
        </>
      )}
    </Box>
  );
};

export default YourFilesTab;
